'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';

import { toast } from '@kit/ui/sonner';

import { validateFile } from '../lib/validation';

/**
 * Media file type returned from uploads
 *
 * Render precedence for the full asset (lightbox / single-mode preview):
 *   1. `preview`     — local object-URL of a freshly-picked browser File
 *   2. `signedUrl`   — server-issued time-limited URL for a private bucket
 *   3. `storagePath` — only useful when the bucket is public; for private
 *                      buckets like dancer-media this is the storage *key*
 *                      (e.g. `userId/headshot/file.jpg`) and will not load
 *                      directly. Some loaders overload this field with a
 *                      signed URL — that's still safe to render.
 *
 * For grid thumbnails, prefer `thumbnailUrl` (server image-transform like
 * 256×256) and fall back through the same chain.
 */
export interface MediaFile {
  id: string;
  storagePath: string | null;
  preview?: string;
  signedUrl?: string;
  thumbnailUrl?: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
  title?: string;
  displayOrder?: number;
  // Video residency signals (BAL-721 / video-playback-resolver). Let the render
  // surfaces route a migrated video to its live source (Mux HLS / R2 CDN /
  // external) via `resolveVideoSource` instead of the dead Supabase-Storage
  // `signedUrl`/`storagePath`. Populated for library rows that carry them;
  // fresh browser uploads leave them undefined (native preview).
  externalUrl?: string | null;
  muxPlaybackId?: string | null;
  muxStatus?: string | null;
  r2Key?: string | null;
}

/**
 * Result type from upload actions
 * Uses a generic data type to allow any shape that has id and storage_path.
 * Optional `signed_url` lets servers return a ready-to-render URL alongside
 * the storage key so the client doesn't have to round-trip for the preview.
 */
export interface UploadResult<
  T extends {
    id: string;
    storage_path: string | null;
    signed_url?: string | null;
  } = {
    id: string;
    storage_path: string | null;
    signed_url?: string | null;
  },
> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Translations for toast messages
 */
export interface UploadTranslations {
  uploadSuccess?: string;
  uploadError?: string;
  deleteSuccess?: string;
  deleteError?: string;
  invalidFile?: string;
  noStoragePath?: string;
}

const DEFAULT_TRANSLATIONS: Required<UploadTranslations> = {
  uploadSuccess: 'File uploaded successfully',
  uploadError: 'Upload failed',
  deleteSuccess: 'File removed',
  deleteError: 'Failed to delete file',
  invalidFile: 'Invalid file',
  noStoragePath: 'Upload failed: No storage path returned',
};

/**
 * Options for the useMediaUpload hook
 */
export interface UseMediaUploadOptions {
  allowedTypes: string[];
  maxFileSize: number;
  /** See FileValidationOptions.allowExtensionFallback (BAL-747). */
  allowExtensionFallback?: boolean;
  maxFiles?: number;
  uploadAction: (formData: FormData) => Promise<UploadResult>;
  deleteAction?: (params: { mediaId: string }) => Promise<UploadResult>;
  onUploadComplete?: (file: MediaFile) => void;
  onDeleteComplete?: (fileId: string) => void;
  /** Custom translations for toast messages */
  translations?: UploadTranslations;
}

/**
 * Hook for managing file uploads with validation and preview generation
 */
export function useMediaUpload(options: UseMediaUploadOptions) {
  const {
    allowedTypes,
    maxFileSize,
    allowExtensionFallback,
    maxFiles = 1,
    uploadAction,
    deleteAction,
    onUploadComplete,
    onDeleteComplete,
    translations: customTranslations,
  } = options;

  const translations = useMemo(
    () => ({ ...DEFAULT_TRANSLATIONS, ...customTranslations }),
    [customTranslations],
  );

  const [files, setFiles] = useState<MediaFile[]>([]);
  const [isPending, startTransition] = useTransition();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Track object-URLs created for image previews so they can be revoked on
  // delete/replace/unmount. A leaked `blob:` URL pins the underlying File in
  // memory until the document unloads.
  const objectUrlsRef = useRef<Set<string>>(new Set());

  const revokePreview = useCallback((file?: MediaFile) => {
    if (file?.preview && objectUrlsRef.current.has(file.preview)) {
      URL.revokeObjectURL(file.preview);
      objectUrlsRef.current.delete(file.preview);
    }
  }, []);

  // Revoke every tracked preview on unmount.
  useEffect(() => {
    const urls = objectUrlsRef.current;
    return () => {
      for (const url of urls) URL.revokeObjectURL(url);
      urls.clear();
    };
  }, []);

  const canAddMore = files.length < maxFiles;

  const validateAndProcessFile = useCallback(
    (file: File): { valid: boolean; error?: string } => {
      const validation = validateFile(file, {
        allowedTypes,
        maxFileSize,
        allowExtensionFallback,
      });
      if (!validation.valid) {
        return { valid: false, error: validation.error };
      }
      return { valid: true };
    },
    [allowedTypes, maxFileSize, allowExtensionFallback],
  );

  const uploadFile = useCallback(
    async (file: File, additionalData?: Record<string, string>) => {
      // Validate
      const validation = validateAndProcessFile(file);
      if (!validation.valid) {
        console.warn('[MediaUpload] Client validation failed:', {
          fileName: file.name,
          error: validation.error,
        });
        toast.error(validation.error || translations.invalidFile);
        return;
      }

      // In-session preview for images: an object-URL (a short `blob:`
      // reference), NEVER a base64 data-URL. The competition apply form
      // persists each MediaFile into audition_applications.fields and re-POSTs
      // it on every autosave/submit; a base64 preview would bloat that body
      // past Vercel's ~4.5 MB server-action cap and fail with 413 (BAL-544).
      // The preview is stripped before the value is persisted (see
      // dynamic-form-renderer) and revoked on delete/unmount.
      let previewUrl: string | undefined;
      if (file.type.startsWith('image/')) {
        previewUrl = URL.createObjectURL(file);
        objectUrlsRef.current.add(previewUrl);
      }

      // Revoke the just-created preview when the upload never yields a rendered
      // MediaFile (validation-less failures below), so a flaky/rejected upload
      // doesn't leak object-URLs (each pins its File in memory until unmount).
      const discardPreview = () => {
        if (previewUrl && objectUrlsRef.current.has(previewUrl)) {
          URL.revokeObjectURL(previewUrl);
          objectUrlsRef.current.delete(previewUrl);
        }
      };

      // Upload
      startTransition(async () => {
        try {
          const formData = new FormData();
          formData.append('file', file);

          // Append any additional data
          if (additionalData) {
            Object.entries(additionalData).forEach(([key, value]) => {
              formData.append(key, value);
            });
          }

          const result = await uploadAction(formData);

          if (!result.success) {
            console.error(
              '[MediaUpload] Upload failed - server returned error:',
              {
                error: result.error,
                fileName: file.name,
              },
            );
            discardPreview();
            toast.error(result.error || translations.uploadError);
            return;
          }

          // Guard against null storage_path (shouldn't happen but types allow it)
          if (!result.data?.storage_path) {
            console.error('[MediaUpload] Upload failed - no storage path:', {
              data: result.data,
              fileName: file.name,
            });
            discardPreview();
            toast.error(translations.noStoragePath);
            return;
          }

          const newFile: MediaFile = {
            id: result.data.id,
            storagePath: result.data.storage_path,
            preview: previewUrl,
            signedUrl: result.data.signed_url ?? undefined,
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type,
          };

          setFiles((prev) => [...prev, newFile]);
          toast.success(translations.uploadSuccess);
          onUploadComplete?.(newFile);
        } catch (err) {
          console.error('[MediaUpload] Upload exception:', {
            error: err instanceof Error ? err.message : 'Unknown error',
            stack: err instanceof Error ? err.stack : undefined,
            fileName: file.name,
          });
          discardPreview();
          toast.error(
            err instanceof Error ? err.message : translations.uploadError,
          );
        }
      });
    },
    [validateAndProcessFile, uploadAction, onUploadComplete, translations],
  );

  const deleteFile = useCallback(
    async (fileId: string): Promise<boolean> => {
      if (!deleteAction) {
        // If no delete action, just remove from local state
        setFiles((prev) => {
          revokePreview(prev.find((f) => f.id === fileId));
          return prev.filter((f) => f.id !== fileId);
        });
        onDeleteComplete?.(fileId);
        return true;
      }

      setDeletingId(fileId);

      try {
        const result = await deleteAction({ mediaId: fileId });

        if (!result.success) {
          toast.error(result.error || translations.deleteError);
          return false;
        }

        startTransition(() => {
          setFiles((prev) => {
            revokePreview(prev.find((f) => f.id === fileId));
            return prev.filter((f) => f.id !== fileId);
          });
        });
        toast.success(translations.deleteSuccess);
        onDeleteComplete?.(fileId);
        return true;
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : translations.deleteError,
        );
        return false;
      } finally {
        setDeletingId(null);
      }
    },
    [deleteAction, onDeleteComplete, translations, revokePreview],
  );

  const setInitialFiles = useCallback((initialFiles: MediaFile[]) => {
    // When the consumer reseeds `files` (e.g. the apply form pushes back the
    // persisted, preview-stripped value after onUploadComplete), any tracked
    // object-URL the incoming set no longer references is now orphaned — revoke
    // it here so it doesn't leak until unmount. Display is unaffected: a
    // reseeded file renders from its `signedUrl`.
    const incomingPreviews = new Set(
      initialFiles.map((f) => f.preview).filter((p): p is string => Boolean(p)),
    );
    for (const url of objectUrlsRef.current) {
      if (!incomingPreviews.has(url)) {
        URL.revokeObjectURL(url);
        objectUrlsRef.current.delete(url);
      }
    }
    setFiles(initialFiles);
  }, []);

  const clearFiles = useCallback(() => {
    for (const url of objectUrlsRef.current) URL.revokeObjectURL(url);
    objectUrlsRef.current.clear();
    setFiles([]);
  }, []);

  return {
    files,
    isPending,
    deletingId,
    canAddMore,
    uploadFile,
    deleteFile,
    setInitialFiles,
    clearFiles,
  };
}
